import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/lib/supabase-types";
import { berlinDateString } from "@/lib/brain/time";
import { BRAIN_POLL } from "@/lib/brain/thresholds";
import type {
  BrainItemNote,
  BrainItemState,
  BrainPriority,
} from "@/lib/brain/types";

export const BRAIN_STATE_KEYS = {
  all: ["brain", "state"] as const,
  itemState: ["brain", "state", "items"] as const,
  notes: ["brain", "state", "notes"] as const,
  acks: (me: string, day: string) =>
    ["brain", "state", "acks", me, day] as const,
};

/** Best-effort: an audit failure never fails the admin's action. */
async function logBrainAudit(
  action: string,
  itemKey: string,
  values: Record<string, Json | undefined>
) {
  try {
    const { data } = await supabase.auth.getUser();
    // supabase-js reports a refused insert in `error`; it doesn't throw.
    const { error } = await supabase.from("audit_logs").insert({
      user_id: data.user?.id ?? null,
      action,
      resource_type: "brain_item",
      resource_id: itemKey,
      new_values: values,
      metadata: { source: "brain" },
    });
    if (error) console.warn("brain audit failed (ignored)", error);
  } catch (e) {
    console.warn("brain audit failed (ignored)", e);
  }
}

export function useBrainState(me: string | undefined) {
  const qc = useQueryClient();
  const day = berlinDateString(new Date());

  const itemState = useQuery({
    queryKey: BRAIN_STATE_KEYS.itemState,
    queryFn: async (): Promise<BrainItemState[]> => {
      const { data, error } = await supabase
        .from("brain_item_state")
        .select(
          "item_key, assignee_id, assign_note, priority_override, last_emailed_at"
        );
      if (error) throw error;
      // priority_override is plain text in the DB; setPriority only writes
      // BrainPriority values.
      return (data ?? []) as BrainItemState[];
    },
    // No signed-in staff user (e.g. the nav badge for an owner): don't fetch.
    enabled: !!me,
    // Polled with the deck: another admin's assignment reaches this deck and
    // badge within one interval.
    ...BRAIN_POLL,
  });

  const acks = useQuery({
    queryKey: BRAIN_STATE_KEYS.acks(me ?? "", day),
    enabled: !!me,
    queryFn: async (): Promise<{ item_key: string; note: string | null }[]> => {
      const { data, error } = await supabase
        .from("brain_acknowledgements")
        .select("item_key, note")
        .eq("admin_id", me)
        .eq("berlin_date", day);
      if (error) throw error;
      return data ?? [];
    },
    ...BRAIN_POLL,
  });

  // ponytail: reads every note ever written; filter to live keys or page by
  // date once the table holds thousands of rows.
  const notesQuery = useQuery({
    queryKey: BRAIN_STATE_KEYS.notes,
    enabled: !!me,
    queryFn: async (): Promise<BrainItemNote[]> => {
      const { data, error } = await supabase
        .from("brain_item_notes")
        .select("id, item_key, author_id, note, created_at")
        .order("created_at");
      if (error) throw error;
      return data ?? [];
    },
    ...BRAIN_POLL,
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: BRAIN_STATE_KEYS.all });

  const upsertState = useMutation({
    mutationFn: async (row: Partial<BrainItemState> & { item_key: string }) => {
      const { error } = await supabase
        .from("brain_item_state")
        .upsert(row, { onConflict: "item_key" });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const ack = useMutation({
    mutationFn: async ({ key, note }: { key: string; note: string | null }) => {
      // The same Berlin clock the read and the undo use, not the column default.
      const { error } = await supabase.from("brain_acknowledgements").insert({
        item_key: key,
        note,
        berlin_date: berlinDateString(new Date()),
      });
      // 23505 = already acknowledged today: treat as done.
      if (error && error.code !== "23505") throw error;
    },
    onSuccess: invalidate,
  });

  const insertNote = useMutation({
    mutationFn: async ({ key, note }: { key: string; note: string }) => {
      const { error } = await supabase
        .from("brain_item_notes")
        .insert({ item_key: key, note });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const unack = useMutation({
    mutationFn: async (key: string) => {
      const { error } = await supabase
        .from("brain_acknowledgements")
        .delete()
        .eq("admin_id", me)
        .eq("item_key", key)
        .eq("berlin_date", day);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  // Stable per query result, so the deck memo downstream only rebuilds on new data.
  const states = useMemo(
    () => new Map((itemState.data ?? []).map((s) => [s.item_key, s])),
    [itemState.data]
  );
  const acknowledged = useMemo(
    () => new Map((acks.data ?? []).map((a) => [a.item_key, a.note])),
    [acks.data]
  );

  // item_key → its notes, oldest first.
  const notes = useMemo(() => {
    const m = new Map<string, BrainItemNote[]>();
    for (const n of notesQuery.data ?? []) {
      const list = m.get(n.item_key);
      if (list) list.push(n);
      else m.set(n.item_key, [n]);
    }
    return m;
  }, [notesQuery.data]);

  return {
    states,
    acknowledged,
    notes,
    isLoading: itemState.isLoading || acks.isLoading,
    acknowledge: async (key: string, note: string | null = null) => {
      await ack.mutateAsync({ key, note: note?.trim() || null });
      void logBrainAudit("brain_acknowledge", key, { note });
    },
    addNote: async (key: string, note: string) => {
      const text = note.trim();
      if (!text) return;
      await insertNote.mutateAsync({ key, note: text });
    },
    unacknowledge: async (key: string) => {
      await unack.mutateAsync(key);
      void logBrainAudit("brain_unacknowledge", key, {});
    },
    assign: async (
      key: string,
      assigneeId: string | null,
      note: string | null = null
    ) => {
      await upsertState.mutateAsync({
        item_key: key,
        assignee_id: assigneeId,
        assign_note: note?.trim() || null,
      });
      void logBrainAudit("brain_assign", key, {
        assignee_id: assigneeId,
        note,
      });
    },
    setPriority: async (key: string, level: BrainPriority | null) => {
      await upsertState.mutateAsync({
        item_key: key,
        priority_override: level,
      });
      void logBrainAudit("brain_priority", key, { priority_override: level });
    },
    markEmailed: async (key: string) => {
      await upsertState.mutateAsync({
        item_key: key,
        last_emailed_at: new Date().toISOString(),
      });
    },
  };
}
